/**
 * Hybrid API Adapter
 * 
 * This adapter uses Electron IPC in desktop mode or HTTP API in browser mode
 */

import { API_BASE } from '../../shared/utils/apiConfig';
import { demoHttpApiCall } from '../../online-demo/services/demoHttpApiBridge';
import { isCloudDemoRuntime } from '../../shared/utils/publicDemoRuntime';
import i18n from '../../i18n';

const la = (key, opts) => i18n.t(`serviceMessages.localApi.${key}`, opts);
const cm = (key, opts) => i18n.t(`serviceMessages.common.${key}`, opts);

const IS_CLOUD_DEMO_BROWSER =
  isCloudDemoRuntime() &&
  typeof window !== 'undefined' &&
  !(typeof window !== 'undefined' && window.electronAPI && typeof window.electronAPI.get === 'function');

/** Coalesce identical in-flight GET requests to avoid connection storms. */
const inflightGetRequests = new Map();

/**
 * API_BASE is typically `http://localhost:5001/api`. Callers often pass `/api/projects/...`,
 * which would otherwise produce `/api/api/projects/...` and 404. Strip the redundant prefix.
 */
function normalizeEndpointForHttp(endpoint) {
  if (endpoint == null || typeof endpoint !== 'string' || !API_BASE) return endpoint;
  const base = String(API_BASE).replace(/\/$/, '');
  if (base.endsWith('/api') && endpoint.startsWith('/api/')) {
    return endpoint.slice(4);
  }
  return endpoint;
}

const EMPTY_DASHBOARD_PAYLOAD = {
  kpis: {},
  stats: {},
  chartRows: [],
  recentItems: [],
};

/**
 * Benign GET 404 responses should not crash list pages / optional lookups.
 * Returns `undefined` when the caller should still throw.
 */
function resolveSoftGet404(apiPath, method) {
  if (method !== 'GET') return undefined;

  const path = String(apiPath).split('?')[0].replace(/\/$/, '');

  if (/employee-portal\/profile/.test(path)) return null;
  if (/\/dashboard$/i.test(path)) return { ...EMPTY_DASHBOARD_PAYLOAD };
  if (/\/(stats|summary|overview)(\/|$)/i.test(path)) return {};
  if (/\/config(\/|$)/i.test(path)) return null;

  if (/\/(\d+|[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})$/i.test(path)) {
    return null;
  }

  return [];
}

// Detect if running in Electron with IPC handlers
const isElectronWithIPC = typeof window !== 'undefined' && 
  window.electronAPI && 
  typeof window.electronAPI.get === 'function';

const isElectron = typeof window !== 'undefined' && window.electronAPI;

console.log('🔌 Environment:', isElectron ? 'Electron Desktop' : 'Browser Mode');
console.log('🔌 Using:', isElectronWithIPC ? 'IPC (JSON files)' : 'HTTP API (PostgreSQL backend)');

/** Merge query object into endpoint for GET (used by timetracking and other filtered lists). */
function withQuery(endpoint, query) {
  if (query == null || typeof query !== 'object' || Array.isArray(query)) {
    return endpoint;
  }
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '') continue;
    if (k === 'signal') continue;
    params.append(k, String(v));
  }
  const qs = params.toString();
  if (!qs) return endpoint;
  const sep = endpoint.includes('?') ? '&' : '?';
  return `${endpoint}${sep}${qs}`;
}

// Generic API call helper
const apiCall = async (endpoint, method = 'GET', data = null) => {
  // Use Electron IPC only if IPC handlers are available
  if (isElectronWithIPC) {
    return await electronApiCall(endpoint, method, data);
  }
  
  // Use HTTP API for browser mode OR Electron without IPC
  return await httpApiCall(endpoint, method, data);
};

// Electron IPC API calls
const electronApiCall = async (endpoint, method = 'GET', data = null) => {
  try {
    let parts = endpoint.split('/').filter(Boolean);
    let collection, id;
    
    // Handle HR endpoints: /hr/employees -> collection='employees'
    if (parts[0] === 'hr' && parts.length >= 2) {
      collection = parts[1];
      id = parts[2];
    } else {
      collection = parts[0];
      id = parts[1];
    }
    
    console.log(`📡 Electron API Call: ${method} ${collection}${id ? `/${id}` : ''}`);

    switch (method) {
      case 'GET':
        if (collection === 'settings') {
          return await window.electronAPI.getSettings();
        } else {
          const items = await window.electronAPI.get(collection);
          if (id) {
            const item = items.find(item => item.id === id);
            if (!item) throw new Error(la('not_found', { collection }));
            return item;
          }
          return items;
        }
        
      case 'POST':
        return await window.electronAPI.post(collection, data);
        
      case 'PUT':
        if (collection === 'settings') {
          return await window.electronAPI.putSettings(data);
        } else {
          return await window.electronAPI.put(collection, id, data);
        }
        
      case 'DELETE':
        return await window.electronAPI.delete(collection, id);
        
      default:
        throw new Error(la('unsupported_method', { method }));
    }
  } catch (error) {
    console.error(`Electron API Call Error for ${endpoint}:`, error);
    throw error;
  }
};

// HTTP API calls (browser mode)
const isNetworkFetchError = (error) =>
  error?.name === 'TypeError'
  || String(error?.message || '').includes('Failed to fetch');

const handleHttpNetworkError = (error, method, endpoint) => {
  const isNetwork = isNetworkFetchError(error);
  if (isNetwork && method === 'GET') {
    if (typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
      console.warn(
        `HTTP API GET ${endpoint}: backend unreachable — start full stack with "npm run dev" (PostgreSQL + API :5001 + web :3001)`,
      );
    } else {
      console.warn(`HTTP API GET ${endpoint}: network error`, error);
    }
    return [];
  }
  if (isNetwork && typeof window !== 'undefined' && /localhost|127\.0\.0\.1/.test(window.location.hostname)) {
    console.error(
      `HTTP API Call Error for ${endpoint}: API unreachable — run "npm run dev" (PostgreSQL + API :5001 + web :3001)`,
    );
  } else {
    console.error(`HTTP API Call Error for ${endpoint}:`, error);
  }
  throw error;
};

const httpApiCall = async (endpoint, method = 'GET', data = null) => {
  try {
    if (IS_CLOUD_DEMO_BROWSER) {
      const path = normalizeEndpointForHttp(endpoint);
      console.log(`📦 Demo in-browser API: ${method} ${path}`);
      return await demoHttpApiCall(path, method, data);
    }

    const path = normalizeEndpointForHttp(endpoint);
    const url = `${API_BASE}${path}`;
    const dedupeKey = method === 'GET' ? `${method}:${url}` : null;

    if (dedupeKey && inflightGetRequests.has(dedupeKey)) {
      return await inflightGetRequests.get(dedupeKey);
    }

    const execute = async () => {
      console.log(`📡 HTTP API Call: ${method} ${url}`);

      const options = {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        mode: 'cors'
      };

      if (data && (method === 'POST' || method === 'PUT' || method === 'PATCH')) {
        options.body = JSON.stringify(data);
      }

      const response = await fetch(url, options);
      console.log(`📡 HTTP API Response status: ${response.status}`);

      if (!response.ok) {
        let errorMessage = cm('api_error', { status: response.status });
        try {
          const errorData = await response.json();
          const nestedError = errorData.error;
          const rawError =
            nestedError != null && typeof nestedError === 'object'
              ? String(nestedError.message || nestedError.detail || JSON.stringify(nestedError))
              : nestedError != null
                ? String(nestedError)
                : '';
          const rawMsg = errorData.message != null ? String(errorData.message) : '';
          const generic =
            !rawError ||
            /^failed to (create|fetch|update|delete)/i.test(rawError) ||
            rawError === `${response.status}`;
          const preferred =
            rawMsg && (!generic || rawMsg.length > rawError.length) ? rawMsg : rawError || rawMsg;
          let resolved = preferred || errorMessage;
          if (resolved != null && typeof resolved === 'object') {
            resolved = JSON.stringify(resolved);
          }
          if (resolved != null && resolved !== '') {
            errorMessage = String(resolved);
          }
        } catch (e) {
          // If we can't parse the error JSON, just use the status code message
        }
        if (method === 'GET' && response.status === 404) {
          const fallback = resolveSoftGet404(path, method);
          if (fallback !== undefined) {
            console.warn('API GET softened (404):', path);
            return fallback;
          }
        }
        console.error('API Error:', errorMessage);
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('📦 Raw Response Data:', responseData);
      console.log('📦 Is Array:', Array.isArray(responseData));
      // Unwrap { data: ... } for list responses; if `data` is null/undefined, prefer [] for arrays
      if (Array.isArray(responseData)) {
        return responseData;
      }
      if (responseData && typeof responseData === 'object' && 'data' in responseData) {
        const d = responseData.data;
        if (Array.isArray(d)) return d;
        if (d == null) {
          const softNull = resolveSoftGet404(path, method);
          if (softNull === null) return null;
          if (method === 'GET') return [];
          return responseData;
        }
        return d;
      }
      return responseData;
    };

    const requestPromise = (async () => {
      try {
        return await execute();
      } catch (error) {
        return handleHttpNetworkError(error, method, endpoint);
      }
    })();

    if (dedupeKey) {
      inflightGetRequests.set(dedupeKey, requestPromise);
      requestPromise.finally(() => {
        inflightGetRequests.delete(dedupeKey);
      });
    }

    return await requestPromise;
  } catch (error) {
    return handleHttpNetworkError(error, method, endpoint);
  }
};

// Client API — must match backend app.use('/api/facturation/clients', …)
const FACTURATION_CLIENTS = '/facturation/clients';
const FACTURATION_FOURNISSEURS = '/facturation/fournisseurs';

// Client API functions
const clients = {
  getAll: async () => {
    const data = await apiCall(FACTURATION_CLIENTS);
    return data;
  },
  
  getClients: async () => {
    const data = await apiCall(FACTURATION_CLIENTS);
    return data;
  },
  
  getById: async (id) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`);
    return data;
  },
  
  getClient: async (id) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`);
    return data;
  },
  
  add: async (client) => {
    const data = await apiCall(FACTURATION_CLIENTS, 'POST', client);
    return data;
  },
  
  createClient: async (client) => {
    const data = await apiCall(FACTURATION_CLIENTS, 'POST', client);
    return data;
  },
  
  update: async (id, updates) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`, 'PUT', updates);
    return data;
  },
  
  updateClient: async (id, updates) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`, 'PUT', updates);
    return data;
  },
  
  delete: async (id) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`, 'DELETE');
    return data;
  },
  
  deleteClient: async (id) => {
    const data = await apiCall(`${FACTURATION_CLIENTS}/${id}`, 'DELETE');
    return data;
  }
};

// Product API functions
const products = {
  getProducts: async () => {
    const data = await apiCall('/products');
    return data;
  },
  
  getProduct: async (id) => {
    const data = await apiCall(`/products/${id}`);
    return data;
  },
  
  createProduct: async (product) => {
    const data = await apiCall('/products', 'POST', product);
    return data;
  },
  
  updateProduct: async (id, updates) => {
    const data = await apiCall(`/products/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteProduct: async (id) => {
    const data = await apiCall(`/products/${id}`, 'DELETE');
    return data;
  }
};

// Settings API functions
const settings = {
  getSettings: async () => {
    const data = await apiCall('/settings');
    return data;
  },
  
  updateSettings: async (settings) => {
    const data = await apiCall('/settings', 'PUT', settings);
    return data;
  },

  createAdminUser: async ({ username, email, password }) => {
    const data = await apiCall('/settings/admin', 'POST', { username, email, password });
    return data;
  },
};

// Employee API functions
const employees = {
  getEmployees: async () => {
    const data = await apiCall('/hr/employees');
    return data;
  },
  
  getEmployee: async (id) => {
    const data = await apiCall(`/hr/employees/${id}`);
    return data;
  },
  
  createEmployee: async (employee) => {
    const data = await apiCall('/hr/employees', 'POST', employee);
    return data;
  },
  
  updateEmployee: async (id, updates) => {
    const data = await apiCall(`/hr/employees/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteEmployee: async (id) => {
    const data = await apiCall(`/hr/employees/${id}`, 'DELETE');
    return data;
  }
};

// Category API functions
const categories = {
  getCategories: async () => {
    const data = await apiCall('/categories');
    return data;
  },
  
  getCategory: async (id) => {
    const data = await apiCall(`/categories/${id}`);
    return data;
  },
  
  createCategory: async (category) => {
    const data = await apiCall('/categories', 'POST', category);
    return data;
  },
  
  updateCategory: async (id, updates) => {
    const data = await apiCall(`/categories/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteCategory: async (id) => {
    const data = await apiCall(`/categories/${id}`, 'DELETE');
    return data;
  }
};

// Document API functions
const documents = {
  getDocuments: async () => {
    const data = await apiCall('/archives');
    return data;
  },
  
  getDocument: async (id) => {
    const data = await apiCall(`/archives/${id}`);
    return data;
  },
  
  createDocument: async (document) => {
    console.log('📤 createDocument API call with:', document);
    const data = await apiCall('/archives', 'POST', document);
    console.log('📥 createDocument API response:', data);
    return data;
  },
  
  updateDocument: async (id, updates) => {
    const data = await apiCall(`/archives/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteDocument: async (id) => {
    const data = await apiCall(`/archives/${id}`, 'DELETE');
    return data;
  }
};

// Dashboard API functions
const dashboard = {
  getStats: async () => {
    // For local development, just return mock data
    return {
      totalSales: 0,
      pendingInvoices: 0,
      completedOrders: 0
    };
  },
  
  refreshStats: async () => {
    return {};
  }
};

// Suppliers API functions
const suppliers = {
  getSuppliers: async () => {
    const data = await apiCall('/suppliers');
    return data;
  },
  
  getSupplier: async (id) => {
    const data = await apiCall(`/suppliers/${id}`);
    return data;
  },
  
  createSupplier: async (supplier) => {
    const data = await apiCall('/suppliers', 'POST', supplier);
    return data;
  },
  
  updateSupplier: async (id, updates) => {
    const data = await apiCall(`/suppliers/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteSupplier: async (id) => {
    const data = await apiCall(`/suppliers/${id}`, 'DELETE');
    return data;
  }
};

// Partners API : clients vs fournisseurs (tables facturation_*)
const partners = {
  getPartners: async (query) => {
    if (query && (query.type === 'supplier' || query.partner_type === 'supplier')) {
      return await apiCall(FACTURATION_FOURNISSEURS);
    }
    return await apiCall(FACTURATION_CLIENTS);
  },
  getPartner: async (id, query) => {
    if (query && (query.type === 'supplier' || query.partner_type === 'supplier')) {
      return await apiCall(`${FACTURATION_FOURNISSEURS}/${id}`);
    }
    return await clients.getClient(id);
  },
  createPartner: async (payload, query) => {
    if (query && (query.type === 'supplier' || query.partner_type === 'supplier')) {
      return await apiCall(FACTURATION_FOURNISSEURS, 'POST', payload);
    }
    return await clients.createClient(payload);
  },
  updatePartner: async (id, updates, query) => {
    if (query && (query.type === 'supplier' || query.partner_type === 'supplier')) {
      return await apiCall(`${FACTURATION_FOURNISSEURS}/${id}`, 'PUT', updates);
    }
    return await clients.updateClient(id, updates);
  },
  deletePartner: async (id, query) => {
    if (query && (query.type === 'supplier' || query.partner_type === 'supplier')) {
      return await apiCall(`${FACTURATION_FOURNISSEURS}/${id}`, 'DELETE');
    }
    return await clients.deleteClient(id);
  }
};

// Payments API — facturation installments table
const FACTURATION_PAYMENTS = '/facturation/payments';

function unwrapFacturationPaymentRow(data) {
  if (!data || typeof data !== 'object') return data;
  if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) return data.data;
  return data;
}

const payments = {
  getPayments: async () => {
    const data = await apiCall(FACTURATION_PAYMENTS);
    if (Array.isArray(data)) return { success: true, data };
    if (data?.data && Array.isArray(data.data)) return data;
    return { success: true, data: Array.isArray(data) ? data : [] };
  },

  getPayment: async (id) => {
    const data = await apiCall(`${FACTURATION_PAYMENTS}/${id}`);
    return { success: true, data: unwrapFacturationPaymentRow(data) };
  },

  createPayment: async (payment) => {
    const data = await apiCall(FACTURATION_PAYMENTS, 'POST', payment);
    return { success: true, data: unwrapFacturationPaymentRow(data) };
  },

  updatePayment: async (id, updates) => {
    const data = await apiCall(`${FACTURATION_PAYMENTS}/${id}`, 'PUT', updates);
    return { success: true, data: unwrapFacturationPaymentRow(data) };
  },

  deletePayment: async (id) => {
    const data = await apiCall(`${FACTURATION_PAYMENTS}/${id}`, 'DELETE');
    return data?.success != null ? data : { success: true, ...data };
  },
};

// Finances API functions
const finances = {
  getFinances: async () => {
    const data = await apiCall('/finances');
    return data;
  },
  
  getFinance: async (id) => {
    const data = await apiCall(`/finances/${id}`);
    return data;
  },
  
  createFinance: async (finance) => {
    const data = await apiCall('/finances', 'POST', finance);
    return data;
  },
  
  updateFinance: async (id, updates) => {
    const data = await apiCall(`/finances/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteFinance: async (id) => {
    const data = await apiCall(`/finances/${id}`, 'DELETE');
    return data;
  },
  
  getStats: async () => {
    const data = await apiCall('/finances/stats');
    return data;
  }
};

// Analytics API functions
const analytics = {
  getStats: async () => {
    const data = await apiCall('/analytics/stats');
    return data;
  },
  
  getSalesAnalysis: async (period = 'month') => {
    const data = await apiCall(`/analytics/sales?period=${period}`);
    return data;
  },
  
  getClientAnalysis: async (period = 'month') => {
    const data = await apiCall(`/analytics/clients?period=${period}`);
    return data;
  },
  
  getPerformanceAnalysis: async (period = 'month') => {
    const data = await apiCall(`/analytics/performance?period=${period}`);
    return data;
  },
  
  getPredictiveAnalysis: async () => {
    const data = await apiCall('/analytics/predictive');
    return data;
  },
  
  getGeographicalAnalysis: async () => {
    const data = await apiCall('/analytics/geographical');
    return data;
  }
};

// Departments API functions
const departments = {
  getDepartments: async () => {
    const data = await apiCall('/management/departments');
    return data;
  },
  
  getDepartment: async (id) => {
    const data = await apiCall(`/management/departments/${id}`);
    return data;
  },
  
  createDepartment: async (department) => {
    const data = await apiCall('/management/departments', 'POST', department);
    return data;
  },
  
  updateDepartment: async (id, updates) => {
    const data = await apiCall(`/management/departments/${id}`, 'PUT', updates);
    return data;
  },
  
  deleteDepartment: async (id) => {
    const data = await apiCall(`/management/departments/${id}`, 'DELETE');
    return data;
  }
};

// Export all services
// Invoices API functions (alias for documents with invoice filtering)
const invoices = {
  getInvoices: async () => {
    try {
      const allDocuments = await documents.getDocuments();
      // Filter for invoice-type documents
      return allDocuments.filter(doc => 
        doc.type === 'invoice' || doc.type === 'facture' || doc.type === 'proforma'
      );
    } catch (error) {
      console.error('Error fetching invoices:', error);
      return [];
    }
  },
  
  getInvoice: documents.getDocument,
  createInvoice: documents.createDocument,
  updateInvoice: documents.updateDocument,
  deleteInvoice: documents.deleteDocument
};

export default {
  clients,
  products,
  settings,
  employees,
  categories,
  documents,
  invoices,
  dashboard,
  suppliers,
  partners,
  payments,
  finances,
  analytics,
  departments,
  
  // Timetracking / Pointage service
  timetracking: {
    getMonthlyEntries: async (params) => {
      const endpoint = withQuery('/timetracking/monthly', params);
      return await apiCall(endpoint, 'GET');
    },
    getMonthlyStats: async (params) => {
      const endpoint = withQuery('/timetracking/monthly/stats', params);
      return await apiCall(endpoint, 'GET');
    },
    getMonthlyCalendar: async (params) => {
      const endpoint = withQuery('/timetracking/monthly/calendar', params);
      return await apiCall(endpoint, 'GET');
    },
    getDailyEntries: async (params) => {
      const endpoint = withQuery('/timetracking/daily', params);
      return await apiCall(endpoint, 'GET');
    },
    getDailyStats: async (params) => {
      const endpoint = withQuery('/timetracking/daily/stats', params);
      return await apiCall(endpoint, 'GET');
    },
    createEntry: async (entry) => {
      return await apiCall('/timetracking/daily', 'POST', entry);
    },
    updateEntry: async (id, updates) => {
      return await apiCall(`/timetracking/daily/${id}`, 'PUT', updates);
    },
    deleteEntry: async (id) => {
      return await apiCall(`/timetracking/daily/${id}`, 'DELETE');
    },
    generateDailyEntries: async (date) => {
      return await apiCall('/timetracking/daily/generate', 'POST', { date });
    }
  },
  
  get: async (endpoint, query) => {
    const q = query && typeof query === 'object' && query.params && !Array.isArray(query.params)
      ? query.params
      : query;
    return await apiCall(withQuery(endpoint, q), 'GET');
  },
  post: async (endpoint, data) => {
    return await apiCall(endpoint, 'POST', data);
  },
  put: async (endpoint, data) => {
    return await apiCall(endpoint, 'PUT', data);
  },
  patch: async (endpoint, data) => {
    return await apiCall(endpoint, 'PATCH', data);
  },
  delete: async (endpoint) => {
    return await apiCall(endpoint, 'DELETE');
  }
};


